/**
 * Illustrations for ATK AppsHub. Open SVG line artwork, no nested panels,
 * no live data. Motion responds to visibility, pointer proximity and touch;
 * reduced-motion users see the final state unless they explicitly turn motion
 * on. Platform names are plain text, never recreated third-party logos.
 */
import { useEffect, useRef, type ReactNode } from "react";
import logoUrl from "@/assets/logo-color.png";
const logo = { url: logoUrl };
import type { GraphicKey } from "./catalogue";

type Variant = "page" | "card";

/** Length of one full sequence, including its internal delays. */
const PLAY_MS: Record<Variant, number> = { page: 3400, card: 3200 };
const HERO_PLAY_MS = 4200;
const REARM_MS = 180;
const VISIBLE_RATIO = 0.08;

function motionAllowed() {
  return true;
}

/**
 * One playback controller per illustration. States: idle, playing, disabled.
 * Every trigger (first visibility, scroll entry, deliberate hover, keyboard
 * focus) goes through play(), which ignores calls while a sequence runs.
 * The finished artwork is the base styling, so ending playback simply removes
 * the temporary class and leaves the complete static drawing.
 */
function usePlaybackController(duration: number) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const figure = ref.current;
    if (!figure) return;
    const card = (figure.closest("[data-motion-card]") as HTMLElement | null) ?? figure;
    let playing = false;
    let armed = true;
    let inView = false;
    let endTimer = 0;
    let rearmTimer = 0;
    let startFrame = 0;
    let holdTimer = 0;

    const stop = () => {
      window.clearTimeout(endTimer);
      window.clearTimeout(holdTimer);
      cancelAnimationFrame(startFrame);
      playing = false;
      figure.classList.remove("is-playing");
    };

    const play = () => {
      if (playing || !motionAllowed() || document.hidden) return;
      playing = true;
      figure.classList.remove("is-playing");
      startFrame = requestAnimationFrame(() => {
        void figure.offsetWidth;
        figure.classList.add("is-playing");
        endTimer = window.setTimeout(() => {
          stop();
          // While the pointer stays on the picture, loop the clip after a short hold.
          if (figure.classList.contains("is-interacting")) holdTimer = window.setTimeout(play, 1200);
        }, duration);
      });
    };

    const restart = () => {
      stop();
      play();
    };

    const io = "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
                inView = true;
                window.clearTimeout(rearmTimer);
                if (armed) {
                  armed = false;
                  play();
                }
              } else if (!entry.isIntersecting) {
                inView = false;
                stop();
                window.clearTimeout(rearmTimer);
                rearmTimer = window.setTimeout(() => {
                  armed = true;
                }, REARM_MS);
              }
            }
          },
          { threshold: [0, VISIBLE_RATIO], rootMargin: "48px 0px" },
        )
      : null;
    io?.observe(figure);

    // IntersectionObserver delivery can arrive late during hydration. Start
    // visible artwork deterministically, then let the observer manage re-entry.
    const initialFrame = requestAnimationFrame(() => {
      const rect = figure.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        inView = true;
        armed = false;
        play();
      }
    });

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const onPointerEnter = (event: PointerEvent) => {
      if (event.pointerType === "touch" || !finePointer.matches) return;
      figure.classList.add("is-interacting");
      if (inView || !io) restart();
    };
    const onPointerLeave = () => figure.classList.remove("is-interacting");
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === "touch" || !finePointer.matches) return;
      const rect = figure.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
      const y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
      figure.style.setProperty("--motion-x", x.toFixed(3));
      figure.style.setProperty("--motion-y", y.toFixed(3));
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      figure.classList.add("is-interacting");
      restart();
      window.setTimeout(() => figure.classList.remove("is-interacting"), Math.min(duration, 2200));
    };
    const onFocusIn = (event: FocusEvent) => {
      const from = event.relatedTarget as Node | null;
      if (from && card.contains(from)) return;
      play();
    };
    const onVisibility = () => {
      if (document.hidden) stop();
    };
    const onPreference = () => {
      if (!motionAllowed()) {
        stop();
        figure.classList.remove("is-interacting");
      } else {
        restart();
      }
    };

    card.addEventListener("pointerenter", onPointerEnter);
    card.addEventListener("pointerleave", onPointerLeave);
    card.addEventListener("pointermove", onPointerMove);
    card.addEventListener("pointerdown", onPointerDown);
    card.addEventListener("focusin", onFocusIn);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("atk-motion-change", onPreference);
    if (!io) play();

    return () => {
      io?.disconnect();
      cancelAnimationFrame(initialFrame);
      stop();
      window.clearTimeout(rearmTimer);
      card.removeEventListener("pointerenter", onPointerEnter);
      card.removeEventListener("pointerleave", onPointerLeave);
      card.removeEventListener("pointermove", onPointerMove);
      card.removeEventListener("pointerdown", onPointerDown);
      card.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("atk-motion-change", onPreference);
    };
  }, [duration]);

  return ref;
}

function Figure({
  variant,
  caption,
  viewBox,
  children,
  hero = false,
}: {
  variant: Variant;
  caption: string;
  viewBox: string;
  children: ReactNode;
  hero?: boolean;
}) {
  const ref = usePlaybackController(hero ? HERO_PLAY_MS : PLAY_MS[variant]);
  return (
    <figure ref={ref} className="atk-figure m-0 w-full">
      <svg
        viewBox={viewBox}
        aria-hidden="true"
        focusable="false"
        className="block h-auto w-full overflow-visible"
      >
        {children}
      </svg>
      {variant === "page" ? (
        <figcaption className="mt-4 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
          {caption}
        </figcaption>
      ) : (
        <span className="sr-only">{caption}</span>
      )}
    </figure>
  );
}

function HubGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure
      variant={variant}
      viewBox="0 0 550 355"
      hero
      caption="From a task list to a focused block in your day, with Automation TK in the middle."
    >
      <path d="M121 110 C185 110 167 183 236 183" className="g-base" />
      <path d="M119 279 C182 279 177 208 236 208" className="g-base" />
      <path d="M318 195 C360 195 345 126 405 126" className="g-base" />
      <path d="M121 110 C185 110 167 183 236 183" className="g-active g-trace" pathLength={100} style={{ animationDelay: "0ms" }} />
      <path d="M119 279 C182 279 177 208 236 208" className="g-active g-trace" pathLength={100} style={{ animationDelay: "600ms", opacity: 0.6 }} />
      <path d="M318 195 C360 195 345 126 405 126" className="g-active g-trace" pathLength={100} style={{ animationDelay: "1600ms" }} />
      <circle cx="121" cy="110" r="2.8" className="g-dot" />
      <circle cx="119" cy="279" r="2.8" className="g-dot" />
      <circle cx="405" cy="126" r="2.8" className="g-dot g-fade-in" style={{ animationDelay: "2300ms" }} />

      <g transform="translate(23 66)">
      <g className="g-pop g-pop-left">
        <path d="M3 0H88L98 10V92H3Z" className="g-paper g-ink" />
        <path d="M88 0V10H98" className="g-fine" />
        <path d="M17 22H81" className="g-sheet-head" />
        <path d="M17 39H81M17 55H81M17 71H81M37 31V78M63 31V78" className="g-muted" />
        <rect x="14" y="46" width="70" height="17" rx="2" className="g-tint g-fade" style={{ animationDelay: "200ms" }} />
        <path d="M20 54H31M43 54H57M69 54H78" className="g-accent-line" />
        <text x="3" y="113" className="g-label">Google Sheets</text>
        <text x="3" y="128" className="g-micro">Tasks with time estimates</text>
      </g>
      </g>

      <g transform="translate(23 250)">
      <g className="g-pop g-pop-left">
        <path d="M0 0H96V43H21L12 53V43H0Z" className="g-paper g-ink" />
        <path d="M15 14H78" className="g-muted-thick" />
        <path d="M15 26H62" className="g-accent-line g-fade" style={{ animationDelay: "900ms" }} />
        <text x="0" y="73" className="g-label">Telegram</text>
        <text x="0" y="88" className="g-micro">A quick update</text>
      </g>
      </g>

      <g transform="translate(236 168)">
      <g>
        <image href={logo.url} x="-8" y="-6" width="96" height="44" preserveAspectRatio="xMidYMid meet" />
        <path d="M6 46H74" className="g-underline" />
      </g>
      </g>

      <g transform="translate(405 67)">
      <g className="g-pop g-pop-right">
        <path d="M0 7H123V160H0Z" className="g-paper g-ink" />
        <path d="M0 37H123M20 0V15M103 0V15" className="g-fine" />
        <text x="13" y="28" className="g-label">Your day</text>
        <path d="M28 47V149M7 69H115M7 95H115M7 121H115" className="g-muted" />
        <text x="7" y="60" className="g-micro" style={{ fontSize: 6 }}>09</text>
        <text x="7" y="86" className="g-micro" style={{ fontSize: 6 }}>10</text>
        <text x="7" y="112" className="g-micro" style={{ fontSize: 6 }}>11</text>
        <g className="g-settle g-task-pop" style={{ animationDelay: "2400ms" }}>
          <rect x="34" y="76" width="80" height="43" rx="3" className="g-tint" />
          <rect x="34" y="76" width="3" height="43" rx="1.5" className="g-dot" />
          <text x="43" y="92" className="g-label" style={{ fontSize: 8 }}>Draft the report</text>
          <text x="43" y="106" className="g-micro" style={{ fontSize: 7 }}>90 minutes</text>
        </g>
        <text x="0" y="184" className="g-label">Time to focus</text>
        <text x="0" y="200" className="g-micro">A place for the next task</text>
      </g>
      </g>
    </Figure>
  );
}

function SchedulerGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="A task and its time estimate become a block in your day.">
      <path d="M166 17H293V156H166Z" className="g-paper g-ink" />
      <path d="M166 43H293M188 11V24M271 11V24" className="g-fine" />
      <path d="M195 52V146M176 73H284M176 137H284" className="g-muted" />
      <text x="178" y="64" className="g-micro" style={{ fontSize: 7 }}>09</text>
      <text x="178" y="118" className="g-micro" style={{ fontSize: 7 }}>10</text>
      <g className="g-pop g-pop-left">
        <text x="23" y="57" className="g-label">Draft the report</text>
        <path d="M22 66H117M22 94H117" className="g-fine" />
        <text x="23" y="84" className="g-accent-text">90 min</text>
        <text x="23" y="118" className="g-micro">From Google Sheets or Telegram</text>
      </g>
      <path d="M126 80C148 80 141 91 196 91" className="g-active g-trace" pathLength={100} />
      <g className="g-settle g-task-pop" style={{ animationDelay: "700ms" }}>
        <rect x="202" y="81" width="81" height="47" rx="3" className="g-tint" />
        <path d="M203 84V125" className="g-accent-bar" />
        <path d="M213 95H271M213 107H248" className="g-accent-soft" />
      </g>
    </Figure>
  );
}

function DatePlannerGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="A flexible route for a trip, holiday, solo date or time with friends.">
      <path d="M21 131L70 90 119 111 169 49 230 72 302 33M42 22L102 59 159 27M240 155L265 111 307 119" className="g-map" />
      <path d="M58 115C107 115 102 61 157 61S222 125 271 100" className="g-base" />
      <path d="M58 115C107 115 102 61 157 61S222 125 271 100" className="g-active g-trace" pathLength={100} />
      <g className="g-pop g-pop-left">
        <circle cx="58" cy="115" r="5" className="g-paper g-stop" />
        <text x="27" y="140" className="g-label">First stop</text>
      </g>
      <g className="g-fade g-pop" style={{ animationDelay: "500ms" }}>
        <circle cx="157" cy="61" r="5" className="g-paper g-stop" />
        <text x="133" y="41" className="g-label">Your pick</text>
      </g>
      <g className="g-fade g-pop g-pop-right" style={{ animationDelay: "1000ms" }}>
        <circle cx="271" cy="100" r="5" className="g-dot" />
        <text x="239" y="126" className="g-label">Next stop</text>
      </g>
    </Figure>
  );
}

function SheetGrid({ label }: { label: string }) {
  return (
    <>
      <path d="M0 0H105V116H0Z" className="g-paper g-ink" />
      <path d="M0 24H105M12 44H94M12 64H94M12 84H94M36 32V102M70 32V102" className="g-muted" />
      <text x="12" y="16" className="g-micro">{label}</text>
    </>
  );
}

function DataSyncGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="The row you choose moves from one sheet to where it needs to be.">
      <g transform="translate(21 28)">
      <g className="g-pop g-pop-left">
        <SheetGrid label="Source" />
        <rect x="8" y="55" width="89" height="19" className="g-tint" />
        <path d="M14 64H29M42 64H62M76 64H91" className="g-accent-line" />
      </g>
      </g>
      <g transform="translate(202 42)">
      <g className="g-pop g-pop-right">
        <SheetGrid label="Destination" />
        <g className="g-settle g-task-pop" style={{ animationDelay: "800ms" }}>
          <rect x="8" y="55" width="89" height="19" className="g-tint" />
          <path d="M14 64H29M42 64H62M76 64H91" className="g-accent-line" />
        </g>
      </g>
      </g>
      <path d="M128 92C160 92 165 106 197 106" className="g-active g-trace" pathLength={100} />
      <path d="M191 101L197 106 191 111" className="g-active g-fade-in" style={{ animationDelay: "900ms" }} />
    </Figure>
  );
}

function GroceryGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="Low household stock feeds straight into the shopping list.">
      <path d="M22 127H139M28 127V138M132 127V138" className="g-fine" />
      <path d="M38 69H73V123H38Z M40 62H71V69H40Z" className="g-paper g-ink" />
      <path d="M91 53H115L124 65V123H91Z M91 53L100 65H124 M100 65V123" className="g-ink g-nofill" />
      <rect x="44" y="105" width="23" height="12" className="g-tint g-stock" />
      <text x="37" y="153" className="g-micro">At home</text>
      <text x="37" y="166" className="g-warn" style={{ fontSize: 8 }}>Milk running low</text>
      <path d="M150 96C181 96 171 53 194 53" className="g-base" />
      <path d="M150 96C181 96 171 53 194 53" className="g-active g-trace" pathLength={100} style={{ animationDelay: "400ms" }} />
      <text x="199" y="26" className="g-label">Shopping list</text>
      <path d="M199 36H305M199 69H305M199 102H305M199 135H305" className="g-muted" />
       <rect x="199" y="48" width="10" height="10" rx="1" className="g-box g-task-pop" />
      <path d="M201 53L204 56 209 49" className="g-active g-trace" pathLength={100} style={{ animationDelay: "1100ms" }} />
       <text x="220" y="56" className="g-label g-task-pop" style={{ fontWeight: 500 }}>Milk</text>
      <rect x="199" y="81" width="10" height="10" rx="1" className="g-box" />
      <text x="220" y="89" className="g-micro">Bread</text>
      <rect x="199" y="114" width="10" height="10" rx="1" className="g-box" />
      <text x="220" y="122" className="g-micro">Rice</text>
    </Figure>
  );
}

function LegalGraphic({ variant }: { variant: Variant }) {
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="A highlighted passage stays connected to its source. Research assistance, not legal advice.">
      <path d="M28 15H135L151 31V158H28Z" className="g-paper g-ink" />
      <path d="M135 15V31H151" className="g-fine" />
      <text x="42" y="44" className="g-label">Public source</text>
      <path d="M42 62H133M42 75H126M42 101H127M42 114H132M42 137H96" className="g-muted" />
       <rect x="38" y="79" width="101" height="17" className="g-tint g-fade g-task-pop" style={{ animationDelay: "150ms" }} />
      <path d="M42 88H130" className="g-accent-thin" />
      <path d="M153 88C183 88 174 60 200 60" className="g-active g-trace" pathLength={100} style={{ animationDelay: "500ms" }} />
      <path d="M202 45V126" className="g-accent-bar" />
      <text x="214" y="56" className="g-label">Extract, linked</text>
      <text x="214" y="72" className="g-label">to its source</text>
      <path d="M214 89H306M214 100H294" className="g-muted" />
      <text x="214" y="119" className="g-accent-text g-fade" style={{ fontSize: 9, animationDelay: "1300ms" }}>Source [1]</text>
    </Figure>
  );
}

function LearnGraphic({ variant }: { variant: Variant }) {
  const steps = ["Understand the idea", "Try a short exercise", "Build your own"];
  // Timeline: step 1 (0ms) → line (500) → step 2 (1000) → line (1500) → step 3 (2000).
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="Understand the idea, practise it, then build your own.">
      <path d="M40 51V79" className="g-active g-trace" pathLength={100} style={{ animationDelay: "500ms", animationDuration: "500ms" }} />
      <path d="M40 101V129" className="g-active g-trace" pathLength={100} style={{ animationDelay: "1500ms", animationDuration: "500ms" }} />
      {steps.map((step, index) => {
        const y = 40 + index * 50;
        const last = index === 2;
        return (
          <g key={step} className={last ? "l-final" : "l-step"} style={{ animationDelay: `${index * 1000}ms` }}>
            <circle cx="40" cy={y} r="11" className={last ? "g-dot" : "g-paper g-stop"} />
            <text x="40" y={y + 3.5} textAnchor="middle" className={last ? "g-on-accent" : "g-accent-text"} style={{ fontSize: 9 }}>
              {index + 1}
            </text>
            <text x="64" y={y + 3.5} className="g-label" style={{ fontSize: 12 }}>{step}</text>
          </g>
        );
      })}
    </Figure>
  );
}

function GuidesGraphic({ variant }: { variant: Variant }) {
  const lines: Array<[number, number]> = [[66, 152], [80, 146], [94, 150], [122, 120], [136, 140]];
  return (
    <Figure variant={variant} viewBox="0 0 330 180" caption="Step-by-step walkthroughs written from real builds.">
      <path d="M40 20H170V160H40Z" className="g-paper g-ink" />
      <path d="M58 44H140" className="g-sheet-head gd-line" pathLength={100} />
      {lines.map(([y, end], i) => (
        <path key={y} d={`M58 ${y}H${end}`} className="g-muted gd-line" pathLength={100} style={{ animationDelay: `${250 + i * 220}ms` }} />
      ))}
      <rect x="54" y="102" width="100" height="12" className="g-tint gd-sweep" style={{ animationDelay: "1450ms" }} />
      <path d="M172 108C200 108 196 76 222 76" className="g-active g-trace" pathLength={100} style={{ animationDelay: "1950ms", animationDuration: "600ms" }} />
      <g className="gd-jump" style={{ animationDelay: "2450ms" }}>
        <text x="228" y="72" className="g-label">Step 3</text>
        <text x="228" y="88" className="g-micro">What we learned</text>
      </g>
    </Figure>
  );
}

export function ToolGraphic({ graphicKey, variant = "page" }: { graphicKey: GraphicKey; variant?: Variant }) {
  switch (graphicKey) {
    case "focus-scheduler":
      return <SchedulerGraphic variant={variant} />;
    case "ai-date-planner":
      return <DatePlannerGraphic variant={variant} />;
    case "data-sync":
      return <DataSyncGraphic variant={variant} />;
    case "grocery-management":
      return <GroceryGraphic variant={variant} />;
    case "cnmi-legal-aide":
      return <LegalGraphic variant={variant} />;
    case "learn":
      return <LearnGraphic variant={variant} />;
    case "guides":
      return <GuidesGraphic variant={variant} />;
    case "hub":
    default:
      return <HubGraphic variant={variant} />;
  }
}
